import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Download, FileSpreadsheet, FileText, Calendar, Search, Package, ShoppingCart, Receipt, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatNumber = (num) => {
  return num?.toLocaleString() || '0';
};

const ReportsDownloadPage = () => {
  const [loading, setLoading] = useState({ purchases: false, sales: false, allPOs: false, allExpenses: false });
  const [purchasesWithDocs, setPurchasesWithDocs] = useState([]);
  const [expensesWithDocs, setExpensesWithDocs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expenseSearchTerm, setExpenseSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('purchases');
  
  // Date filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchPurchasesWithDocs();
    fetchExpensesWithDocs();
  }, [startDate, endDate]);

  const fetchPurchasesWithDocs = async () => {
    try {
      let url = `${API_URL}/reports/purchases-with-documents`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setPurchasesWithDocs(response.data);
    } catch (error) {
      console.error('Failed to fetch purchases:', error);
    }
  };

  const fetchExpensesWithDocs = async () => {
    try {
      let url = `${API_URL}/reports/expenses-with-documents`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setExpensesWithDocs(response.data);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    }
  };

  const handleDownloadPurchasesReport = async () => {
    setLoading(prev => ({ ...prev, purchases: true }));
    try {
      let url = `${API_URL}/reports/download/purchases`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url, { responseType: 'blob' });
      downloadFile(response.data, `purchases_report${startDate && endDate ? `_${startDate}_to_${endDate}` : ''}.xlsx`);
      toast.success('Purchases report downloaded');
    } catch (error) {
      toast.error('Failed to download purchases report');
    } finally {
      setLoading(prev => ({ ...prev, purchases: false }));
    }
  };

  const handleDownloadSalesReport = async () => {
    setLoading(prev => ({ ...prev, sales: true }));
    try {
      let url = `${API_URL}/reports/download/sales`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url, { responseType: 'blob' });
      downloadFile(response.data, `sales_report${startDate && endDate ? `_${startDate}_to_${endDate}` : ''}.xlsx`);
      toast.success('Sales report downloaded');
    } catch (error) {
      toast.error('Failed to download sales report');
    } finally {
      setLoading(prev => ({ ...prev, sales: false }));
    }
  };

  // Extract filename from Content-Disposition (handles filename* RFC 5987, quoted, plain)
  const parseFilename = (contentDisposition) => {
    if (!contentDisposition) return null;
    // Prefer the RFC 5987 filename* value (UTF-8 encoded)
    const star = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (star) {
      try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')); } catch { /* fall through */ }
    }
    // Fall back to plain filename= (with or without quotes)
    const plain = contentDisposition.match(/filename=("([^"]+)"|([^;]+))/i);
    if (plain) return (plain[2] || plain[3] || '').trim();
    return null;
  };

  const downloadFile = (data, filename, contentType) => {
    const blob = new Blob([data], contentType ? { type: contentType } : undefined);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
  };

  const handleDownloadPurchaseOrder = async (purchaseId, productName) => {
    try {
      const response = await axios.get(`${API_URL}/reports/download/purchase-order/${purchaseId}`, { responseType: 'blob' });
      const serverFilename = parseFilename(response.headers['content-disposition']);
      const filename = serverFilename || `PO_${purchaseId.slice(0, 8)}.bin`;
      downloadFile(response.data, filename, response.headers['content-type']);
      toast.success('Purchase order downloaded');
    } catch (error) {
      toast.error('Failed to download purchase order');
    }
  };

  const handleDownloadExpenseDoc = async (expenseId, description) => {
    try {
      const response = await axios.get(`${API_URL}/reports/download/expense-document/${expenseId}`, { responseType: 'blob' });
      const serverFilename = parseFilename(response.headers['content-disposition']);
      const filename = serverFilename || `Expense_${expenseId.slice(0, 8)}.bin`;
      downloadFile(response.data, filename, response.headers['content-type']);
      toast.success('Expense document downloaded');
    } catch (error) {
      toast.error('Failed to download expense document');
    }
  };

  const handleDownloadAllPOs = async () => {
    const docsToDownload = filteredPurchases.filter(p => p.has_document);
    if (docsToDownload.length === 0) {
      toast.error('No documents to download');
      return;
    }
    
    setLoading(prev => ({ ...prev, allPOs: true }));
    let downloaded = 0;
    
    for (const purchase of docsToDownload) {
      try {
        await handleDownloadPurchaseOrder(purchase.id, purchase.product_name);
        downloaded++;
        await new Promise(r => setTimeout(r, 300)); // Prevent overwhelming browser
      } catch (e) {
        console.error('Failed to download:', purchase.id);
      }
    }
    
    setLoading(prev => ({ ...prev, allPOs: false }));
    toast.success(`Downloaded ${downloaded} purchase order documents`);
  };

  const handleDownloadAllExpenseDocs = async () => {
    const docsToDownload = filteredExpenses.filter(e => e.has_document);
    if (docsToDownload.length === 0) {
      toast.error('No documents to download');
      return;
    }
    
    setLoading(prev => ({ ...prev, allExpenses: true }));
    let downloaded = 0;
    
    for (const expense of docsToDownload) {
      try {
        await handleDownloadExpenseDoc(expense.id, expense.description);
        downloaded++;
        await new Promise(r => setTimeout(r, 300));
      } catch (e) {
        console.error('Failed to download:', expense.id);
      }
    }
    
    setLoading(prev => ({ ...prev, allExpenses: false }));
    toast.success(`Downloaded ${downloaded} expense documents`);
  };

  const filteredPurchases = purchasesWithDocs.filter(p => 
    p.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredExpenses = expensesWithDocs.filter(e => 
    e.category?.toLowerCase().includes(expenseSearchTerm.toLowerCase()) ||
    e.description?.toLowerCase().includes(expenseSearchTerm.toLowerCase())
  );

  const totalPurchasesValue = filteredPurchases.reduce((sum, p) => sum + (p.total_cost || 0), 0);
  const totalExpensesValue = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const purchaseDocsCount = filteredPurchases.filter(p => p.has_document).length;
  const expenseDocsCount = filteredExpenses.filter(e => e.has_document).length;

  return (
    <div className="space-y-6" data-testid="reports-download-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">Download Reports</h1>
          <p className="text-muted-foreground">Download purchases, sales reports, and supporting documents</p>
        </div>
      </div>

      {/* Date Filter */}
      <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-primary" />
          Filter by Date Period
        </h2>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium mb-2">Start Date</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">End Date</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date-input" />
          </div>
          <Button variant="outline" onClick={() => { setStartDate(''); setEndDate(''); }}>
            Clear Dates
          </Button>
        </div>
      </div>

      {/* Download Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
          <h3 className="text-lg font-semibold flex items-center">
            <Package className="w-5 h-5 mr-2 text-blue-600" />
            Purchases Report
          </h3>
          <p className="text-sm text-muted-foreground mt-1">All purchases with product, supplier, customer, and cost details.</p>
          <Button onClick={handleDownloadPurchasesReport} disabled={loading.purchases} className="mt-4 w-full" data-testid="download-purchases-btn">
            {loading.purchases ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Download Purchases Excel
          </Button>
        </div>

        <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
          <h3 className="text-lg font-semibold flex items-center">
            <ShoppingCart className="w-5 h-5 mr-2 text-green-600" />
            Sales Report
          </h3>
          <p className="text-sm text-muted-foreground mt-1">All orders with customer, products, quantities, and revenue details.</p>
          <Button onClick={handleDownloadSalesReport} disabled={loading.sales} className="mt-4 w-full" data-testid="download-sales-btn">
            {loading.sales ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Download Sales Excel
          </Button>
        </div>
      </div>

      {/* Tabs for Documents */}
      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('purchases')}
            className={`flex-1 px-6 py-4 text-sm font-medium ${activeTab === 'purchases' ? 'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:bg-muted/50'}`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Purchase Orders ({purchaseDocsCount} docs)
          </button>
          <button
            onClick={() => setActiveTab('expenses')}
            className={`flex-1 px-6 py-4 text-sm font-medium ${activeTab === 'expenses' ? 'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:bg-muted/50'}`}
          >
            <Receipt className="w-4 h-4 inline mr-2" />
            Expense Documents ({expenseDocsCount} docs)
          </button>
        </div>

        {/* Purchase Orders Tab */}
        {activeTab === 'purchases' && (
          <div>
            <div className="p-4 border-b border-border flex items-center justify-between gap-4">
              <div className="flex items-center space-x-2 flex-1">
                <Search className="w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search purchases..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
              </div>
              <div className="text-sm text-muted-foreground">
                Total: RWF {formatNumber(totalPurchasesValue)}
              </div>
              <Button onClick={handleDownloadAllPOs} disabled={loading.allPOs || purchaseDocsCount === 0} variant="outline" size="sm">
                {loading.allPOs ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download All ({purchaseDocsCount})
              </Button>
            </div>
            
            <div className="overflow-x-auto max-h-96">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Product</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Supplier</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Total</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Document</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredPurchases.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No purchases found</td></tr>
                  ) : (
                    filteredPurchases.map((p) => (
                      <tr key={p.id} className="hover:bg-muted/30">
                        <td className="px-4 py-3 text-sm">{p.created_at?.slice(0, 10)}</td>
                        <td className="px-4 py-3 text-sm font-medium">{p.product_name}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{p.supplier_name}</td>
                        <td className="px-4 py-3 text-sm text-right">RWF {formatNumber(p.total_cost)}</td>
                        <td className="px-4 py-3 text-center">
                          {p.has_document ? (
                            <Button variant="ghost" size="sm" onClick={() => handleDownloadPurchaseOrder(p.id, p.product_name)}>
                              <Download className="w-4 h-4 text-blue-600" />
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Expenses Tab */}
        {activeTab === 'expenses' && (
          <div>
            <div className="p-4 border-b border-border flex items-center justify-between gap-4">
              <div className="flex items-center space-x-2 flex-1">
                <Search className="w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search expenses..."
                  value={expenseSearchTerm}
                  onChange={(e) => setExpenseSearchTerm(e.target.value)}
                  className="flex-1"
                />
              </div>
              <div className="text-sm text-muted-foreground">
                Total: RWF {formatNumber(totalExpensesValue)}
              </div>
              <Button onClick={handleDownloadAllExpenseDocs} disabled={loading.allExpenses || expenseDocsCount === 0} variant="outline" size="sm">
                {loading.allExpenses ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download All ({expenseDocsCount})
              </Button>
            </div>
            
            <div className="overflow-x-auto max-h-96">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Category</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Payment</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Amount</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Document</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredExpenses.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No expenses found</td></tr>
                  ) : (
                    filteredExpenses.map((e) => (
                      <tr key={e.id} className="hover:bg-muted/30">
                        <td className="px-4 py-3 text-sm">{e.date?.slice(0, 10)}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex px-2 py-0.5 rounded text-xs bg-gray-100">{e.category}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{e.description?.slice(0, 40)}{e.description?.length > 40 ? '...' : ''}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{e.payment_method}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-red-600">RWF {formatNumber(e.amount)}</td>
                        <td className="px-4 py-3 text-center">
                          {e.has_document ? (
                            <Button variant="ghost" size="sm" onClick={() => handleDownloadExpenseDoc(e.id, e.description)}>
                              <Download className="w-4 h-4 text-blue-600" />
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsDownloadPage;
